// Simple implementation of vue-wait compatible with Vue 3

import { reactive } from 'vue';
import type { App } from 'vue';

export interface Wait {
    is(loader: string | string[]): boolean;
    any(): boolean;
    start(loader: string): void;
    end(loader: string): void;
    waiting(): string[];
}

// The single source of wait state. `install` both provides this object for
// injection and registers it as a global property, so the injected `$wait` and
// this exported `wait` are the same instance — `useWait` can fall back to it
// when called outside an active component instance and still read one shared
// state. Keep `install` providing this exact object so that guarantee holds.
const waiters = reactive(new Map<string, number>());

function matchesWildcard(pattern: string, key: string): boolean {
    if (!pattern.includes('*')) {
        return pattern === key;
    }
    // Convert wildcard pattern to regex: 'creating.account.*' -> /^creating\.account\..*$/
    const regexPattern = pattern
        .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape special regex chars except *
        .replace(/\*/g, '.*'); // Replace * with .*
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(key);
}

function isLoaderActive(loader: string): boolean {
    if (!loader.includes('*')) {
        return waiters.has(loader);
    }
    // Wildcard matching: check if any active waiter matches the pattern
    for (const key of waiters.keys()) {
        if (matchesWildcard(loader, key)) {
            return true;
        }
    }
    return false;
}

export const wait: Wait = {
    is(loader: string | string[]): boolean {
        if (Array.isArray(loader)) {
            return loader.some(isLoaderActive);
        }
        return isLoaderActive(loader);
    },

    any(): boolean {
        return waiters.size > 0;
    },

    start(loader: string): void {
        const count = waiters.get(loader) ?? 0;
        waiters.set(loader, count + 1);
    },

    end(loader: string): void {
        const count = waiters.get(loader);
        if (!count) {
            return;
        }

        if (count <= 1) {
            waiters.delete(loader);
            return;
        }

        waiters.set(loader, count - 1);
    },

    waiting(): string[] {
        return [...waiters.keys()];
    },
};

export default {
    install(app: App): void {
        app.config.globalProperties.$wait = wait;
        app.provide('$wait', wait);
    },
};
